import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Copy } from 'lucide-react';
import { BsInfoCircleFill } from 'react-icons/bs';
import { Checkbox } from 'antd';
import Swal from 'sweetalert2';
import { toast } from 'sonner';
import apiUtils from 'utils/apiutils';
import { useCooldownAction } from 'hooks/useCooldownAction';
import { LIVEIP } from 'constants/constant';

const ExchangeDetailUI = () => {
  const { exchange, mode } = useParams();
  const [isChecked, setIsChecked] = useState(false);
  const [isSpotEnabled, setIsSpotEnabled] = useState(false);
  const [isFuturesEnabled, setIsFuturesEnabled] = useState(true);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ipWhitelist, setIpWhitelist] = useState('');
  const [exchangeConfig, setExchangeConfig] = useState({
    ApiKey: false,
    Secret: false,
    Passphrase: false,
  });

  const [formData, setFormData] = useState({
    api_name: '',
    api_key: '',
    api_secret: '',
    api_passphrase: '',
    enabled_spot: false,
    enabled_futures: true,
    exchange: exchange?.toUpperCase(),
    trade_type: mode?.toUpperCase()
  });

  useEffect(() => {
    formData.enabled_spot = isSpotEnabled;
    formData.enabled_futures = isFuturesEnabled;
  }, [isSpotEnabled, isFuturesEnabled]);



  const [supportedTypes, setSupportedTypes] = useState([]);
  const runWithCooldown = useCooldownAction();
  const ipList = LIVEIP.join(", "); // or use "\n" if you prefer new lines

  // useEffect(() => {
  //   const fetchExchangeTypes = async () => {
  //     try {
  //       const response = await apiUtils.post(
  //         '/api/get_exchange_types',
  //         { jwt, exchange: exchange.toUpperCase() },
  //         { showToast: false }
  //       );
  //       if (response?.Success) {
  //         // Extract IP whitelist
  //         const ipList = response.Success.find(item => item.IP)?.IP || [];
  //         setIpWhitelist(ipList.join(' '));

  //         // Extract exchange-specific configuration
  //         const exchangeData = response.Success.find(
  //           item => item.Name?.toLowerCase() === exchange?.toLowerCase()
  //         );
  //         if (exchangeData) {
  //           setExchangeConfig({
  //             ApiKey: exchangeData.ApiKey || false,
  //             Secret: exchangeData.Secret || false,
  //             Passphrase: exchangeData.Passphrase || false,
  //           });
  //           // Assume SPOT and FUTURES support based on ApiKey presence (adjust as per actual API)
  //           const types = [];
  //           if (exchangeData.ApiKey) types.push('SPOT');
  //           if (exchangeData.ApiKey && exchangeData.Name !== 'Deribit') types.push('FUTURES');
  //           setSupportedTypes(types);
  //         } else {
  //           throw new Error(`Configuration not found for ${exchange}.`);
  //         }
  //       } else {
  //         throw new Error('No supported types found.');
  //       }
  //     } catch (error) {
  //       toast.error(error?.message || 'Failed to fetch exchange types.');
  //     }
  //   };

  //   if (jwt) {
  //     fetchExchangeTypes();
  //   } else {
  //     toast.error('Authentication required. Please log in.');
  //     navigate('/');
  //   }
  // }, [jwt, exchange, navigate]);

  const handleSuccessButtonClick = (text) => {
    Swal.fire({
      title: 'Success!',
      text: text,
      icon: 'success',
      confirmButtonText: 'OK',
      customClass: {
        popup: 'bg-white dark:bg-gray-900 border border-purple-700 text-black dark:text-white rounded-lg shadow-lg',
        confirmButton: 'bg-purple-700 text-black px-4 py-2 rounded font-semibold',
      },
      buttonsStyling: false,
    }).then(() => {
      navigate('/dashboard/api');
    });
  };

  const handleAPI = async () => {



    await runWithCooldown(async () => {
      setLoading(true);
      try {
        const payload = {
          formData
        };
        const response = await apiUtils.post('/api/connect_api', formData, { showToast: true });

        if (response) {
          handleSuccessButtonClick('API connected successfully.');
        } else {

        }
      } catch (err) {

      } finally {
        setLoading(false);
      }
    })
  };

  const handleBack = () => {
    navigate('/dashboard/connect-exchange');
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('IP Whitelist copied to clipboard!');
    }).catch((err) => {
      console.error('Failed to copy: ', err);
      toast.error('Failed to copy IP whitelist.');
    });
  };

  const handleCheckboxChange = (e) => {
    setIsChecked(e.target.checked);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'api_name') {
      const filteredValue = value.replace(/[^a-zA-Z_]/g, '');
      setFormData((prev) => ({ ...prev, [name]: filteredValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };




  const renderUI = (exchangeName, isLiveMode) => {
    return (
      <div className="text-white p-4 md:p-0">
        <div className="bg-secondary backdrop-blur-lg p-6 md:p-10 rounded-2xl shadow-2xl m-4  border border-primary/30">

          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="p-2 rounded-full bg-primary transition"
              >
                <ArrowLeft size={20} className="text-secondary" />
              </button>
              <h2 className="text-lg md:text-2xl font-bold text-primary tracking-wide">
                {isLiveMode
                  ? `Connect ${exchangeName} Account`
                  : `Add ${exchangeName} Paper Trading Account`}
              </h2>
            </div>
          </div>

          {/* Guide / Info Section */}
          <div className="p-5 w-full rounded-xl mb-8 border border-primary/50 bg-gray-800/70">
            {isLiveMode ? (
              <>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
                  <h3 className="text-base md:text-lg font-semibold text-Profitslatheme-white">
                    How to connect your {exchangeName} API
                  </h3>
                  <a
                    href="#"
                    className="mt-3 md:mt-0 px-3 py-1 text-xs font-medium text-secondary bg-primary rounded-md shadow hover:opacity-80 transition"
                  >
                    Full Guide
                  </a>
                </div>
                <ol className="text-gray-300 list-decimal list-inside text-sm space-y-2">
                  <li>Log in to your {exchangeName} account → API Settings</li>
                  <li>
                    Enable IP Whitelisting → Copy/paste IPs below
                    <span className="block text-white text-xs mt-1">
                      (Whitelist is provided below)
                    </span>
                  </li>
                  <li>Ensure Spot/Futures trading permissions are enabled</li>
                  <li>Fill in API Key & Secret in the form below</li>
                </ol>
              </>
            ) : (
              <>
                <div className="flex gap-2 my-2 items-center">
                  <span className="text-primary text-lg">🔒</span>
                  <strong className="text-xl text-white uppercase">Paper Trading Mode</strong>
                </div>
                <p className="ml-7 text-xs md:text-sm text-white leading-relaxed">
                  {import.meta.env.VITE_PROJECT} Paper Trading lets you practice strategies,
                  test bots, and explore exchanges safely — with full order simulation.
                </p>
                <p className="ml-7 text-xs md:text-sm text-white mt-2">
                  You can also adjust balances manually for any coin.
                </p>
              </>
            )}
          </div>

          {/* Form Fields */}
          <div className="space-y-5">
            {/* Account Label */}
            <div>
              <label className="block text-xs uppercase tracking-wide font-semibold text-primary">
                API Name


              </label>
              <input
                type="text"
                name="api_name"
                value={formData.api_name}
                onChange={handleInputChange}
                className="w-full p-3 bg-gray-700/60 border border-gray-600 rounded-lg mt-2 text-sm text-white placeholder-gray-400 focus:ring-2 focus:ring-primary outline-none"
                placeholder="Enter API Name"
              />
            </div>

            {/* Live Mode Fields */}
            {isLiveMode ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {!exchangeConfig.ApiKey && (
                  <div>
                    <label className="block text-xs font-semibold text-primary">
                      API Key
                    </label>
                    <input
                      type="text"
                      name="api_key"
                      value={formData.api_key}
                      onChange={handleInputChange}
                      className="w-full p-3 bg-gray-700/60 border border-gray-600 rounded-lg mt-2 text-sm text-white placeholder-white focus:ring-2 focus:ring-primary outline-none"
                      placeholder="Enter API Key"
                    />
                  </div>
                )}
                {!exchangeConfig.Secret && (
                  <div>
                    <label className="block text-xs font-semibold text-primary">
                      Secret
                    </label>
                    <input
                      type="text"
                      name="api_secret"
                      value={formData.api_secret}
                      onChange={handleInputChange}
                      className="w-full p-3 bg-gray-700/60 border border-gray-600 rounded-lg mt-2 text-sm text-white placeholder-white focus:ring-2 focus:ring-primary outline-none"
                      placeholder="Enter Secret"
                    />
                  </div>
                )}
                {/* {!exchangeConfig.Passphrase && (
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-primary">
                      Passphrase
                    </label>
                    <input
                      type="text"
                      name="api_passphrase"
                      value={formData.api_passphrase}
                      onChange={handleInputChange}
                      className="w-full p-3 bg-gray-700/60 border border-gray-600 rounded-lg mt-2 text-sm text-white placeholder-white focus:ring-2 focus:ring-primary outline-none"
                      placeholder="Enter Passphrase"
                    />
                  </div>
                )} */}
              </div>
            ) : (
              <>

              </>
            )}

            {/* Toggles */}
            {(!supportedTypes.includes("SPOT") || !supportedTypes.includes("FUTURES")) && (
              <div className="flex flex-col md:flex-row gap-5">

                {/* <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="spot-toggle"
                    className="hidden"
                    checked={isSpotEnabled}
                    onChange={(e) => setIsSpotEnabled(e.target.checked)}
                  />
                  <label
                    htmlFor="spot-toggle"
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <div
                      className={`relative w-12 h-6 rounded-full transition-colors ${isSpotEnabled ? "bg-primary" : "bg-gray-600"
                        }`}
                    >
                      <div
                        className={`absolute w-6 h-6 bg-white rounded-full transform transition-transform ${isSpotEnabled ? "translate-x-6" : "translate-x-0"
                          }`}
                      ></div>
                    </div>
                    <span className="text-xs text-primary  font-semibold">Enable Spot</span>
                  </label>
                </div> */}


                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="futures-toggle"
                    className="hidden"
                    disabled
                    checked={isFuturesEnabled}
                    onChange={(e) => setIsFuturesEnabled(e.target.checked)}
                  />
                  <label
                    htmlFor="futures-toggle"
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <div
                      className={`relative w-12 h-6 rounded-full transition-colors ${isFuturesEnabled ? "bg-primary" : "bg-gray-600"
                        }`}
                    >
                      <div
                        className={`absolute w-6 h-6 bg-white rounded-full transform transition-transform ${isFuturesEnabled ? "translate-x-6" : "translate-x-0"
                          }`}
                      ></div>
                    </div>
                    <span className="text-xs text-primary  font-semibold">Enable Futures</span>
                  </label>
                </div>
              </div>
            )}

            {isLiveMode && (
              <div className="mt-6">
                <p className="text-primary text-xs mb-2 font-semibold">
                  Whitelist these IPs on your exchange:
                </p>

                <div className="relative">
                  <input
                    type="text"
                    value={ipList}
                    className="w-full p-3 text-xs bg-gray-800/60 rounded-lg text-white pr-10 border border-gray-600 focus:ring-2 focus:ring-primary outline-none"
                    readOnly
                  />
                  <button
                    className="absolute right-3 top-2 text-primary hover:opacity-70"
                    onClick={() => handleCopyToClipboard(ipList)}
                  >
                    <Copy size={16} />
                  </button>
                </div>

                <div className="text-xs flex gap-2 mt-3 items-center text-muted-foreground">
                  <BsInfoCircleFill className="text-primary" size={16} />
                  <span>Use whitelisted IPs for maximum security.</span>
                </div>
              </div>
            )}


            {/* Checkbox Agreement */}
            <Checkbox
              id="tradeCheckbox"
              checked={isChecked}
              onChange={handleCheckboxChange}
              className="text-primary text-sm"
            >
              I authorize {import.meta.env.VITE_PROJECT} to execute trades via this API.
            </Checkbox>

            {/* Buttons */}
            <div className="flex flex-col md:flex-row justify-between items-center mt-6 gap-4">
              {isLiveMode && (
                <a
                  href={`https://accounts.${exchangeName?.toLowerCase()}.com/en/register`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary text-sm underline"
                >
                  Don’t have an account? Create one
                </a>
              )}
              <div className="flex gap-3">
                <Link
                  to="/dashboard/connect-exchange"
                  className="px-6 py-2 rounded-full font-semibold bg-gray-700/60 text-white hover:bg-gray-600 transition"
                >
                  Cancel
                </Link>
                <button
                  id="connectButton"
                  type="button"
                  disabled={!isChecked || loading}
                  onClick={handleAPI}
                  className={`px-6 py-2 rounded-full font-semibold text-secondary bg-primary shadow-lg transition ${!isChecked || loading
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:opacity-90"
                    }`}
                >
                  {loading ? "Connecting..." : "Connect"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  return renderUI(exchange, mode === 'Live');
};

export default ExchangeDetailUI;